import { useEffect, useState } from 'react';
import { climbScansService } from '../../../services/climb-scans';
import type { RecentClimbScan } from '../../../types/climb-scan';
import type { ClimbEditorPlace, ClimbEditorType } from './useClimbEditorRoute';
import { useAuth } from '../../../hooks/useAuth';

interface RecentClimbScansArgs {
  place: ClimbEditorPlace;
  /** Absent until the place has loaded; the fetch waits for it. */
  locationId: string | undefined;
  type: ClimbEditorType;
  /**
   * Whether a climb is being put up rather than corrected. A climb made off a
   * scan is made *through* the scan (see `climbScansService.publishClimbOnScan`),
   * and a climb that already exists cannot be.
   */
  creating: boolean;
}

export interface RecentClimbScans {
  scans: RecentClimbScan[];
  /**
   * Whether a photo taken now is read for holds on the spot
   * (`ClimbScans::LiveScan`): asked here because it is what the page reads
   * before any photo is in.
   */
  liveScan: boolean;
}

const NONE: RecentClimbScans = { scans: [], liveScan: false };

/**
 * The walls this gym has had scanned, newest first.
 *
 * The setters photograph a reset in the panel and the scan finds every hold on
 * each wall, so the picture for the next problem is usually one of these — and
 * picked as a scan, it comes with its holds as placeholders to choose from.
 *
 * **Gyms, and for anybody signed in** — unlike the last walls offered beside
 * them (`useRecentClimbPhotos`), which are the staff's own recent work. The
 * server decides which walls: every scan to the gym's climb staff, and to a
 * member only the walls with a climb up on them (`ClimbScan.offered_walls`).
 */
export function useRecentClimbScans({
  place,
  locationId,
  type,
  creating,
}: RecentClimbScansArgs): RecentClimbScans {
  const [walls, setWalls] = useState<RecentClimbScans>(NONE);
  const { user } = useAuth();
  const signedIn = !!user;

  useEffect(() => {
    if (!creating || place !== 'gym' || !locationId || !signedIn) {
      setWalls(NONE);
      return;
    }

    let cancelled = false;
    climbScansService
      .getRecentScans(locationId, type === 'Route' ? 'route' : 'boulder')
      // A list that will not load costs the setter a shortcut, not the page.
      .then((loaded) => {
        if (!cancelled) {
          setWalls({ scans: loaded.scans.filter((scan) => !!scan.url), liveScan: loaded.liveScan });
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [creating, place, locationId, signedIn, type]);

  return walls;
}
